import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import ButtonToast, { useButtonToast } from '@/components/Custom/ButtonToast';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import Placeholder from '@/components/Custom/Placeholder';
import { useUploadEntityAvatars } from '@/components/Entity/Entity.utils';
import type { EntityListField } from '@/components/Entity/EntityList.types';
import { useNotifyStore } from '@/components/Notify/Notify.utils';
import {
  type MemberType,
  MemberTypeSelector,
} from '@/components/People/PeopleFilterSelector';
import ContentBoundary from '@/components/UI/ContentBoundary';
import Box from '@/components/Unistyles/Box';
import { MAXIMUM_PERSON_TEAM_COUNT } from '@/utils/constants';
import getEmptyEntity from '@/utils/getEmptyEntity';
import getRandomColor from '@/utils/getRandomColor';
import type { ManagedMemberInput, MemberAddState } from './MemberAdd.types';
import MemberAddInvite from './MemberAddInvite';
import MemberAddList from './MemberAddList';

type MemberAddFormValues = {
  members: EntityListField[];
};

const resolver = Yup.object().shape({
  members: Yup.array()
    .of(
      Yup.object().shape({
        title: Yup.string().notOneOf([''], 'Title cannot be blank').required(),
      }),
    )
    .max(MAXIMUM_PERSON_TEAM_COUNT)
    .required(),
});

export default function MemberAdd({
  initialMemberType,
  state,
}: {
  initialMemberType: MemberType;
  state: MemberAddState;
}) {
  const { dismissAll } = useRouter();
  const notify = useNotifyStore((state) => state.show);
  const { upload: uploadEntityAvatars } = useUploadEntityAvatars('person');
  const [toast, setToast] = useButtonToast();
  const [memberType, setMemberType] = useState(initialMemberType);
  const form = useForm({
    defaultValues: {
      members: [getEmptyEntity(getRandomColor())],
    },
    resolver: yupResolver(resolver),
  });

  const onPressSave = async (values: MemberAddFormValues) => {
    if (state.status !== 'ready') {
      return;
    }

    let newMemberAvatars: Record<string, string> = {};
    const newMembersWithAvatar = values.members.filter(
      (member) => member.localAvatarUrl,
    );

    if (newMembersWithAvatar.length) {
      try {
        newMemberAvatars = await uploadEntityAvatars(newMembersWithAvatar);
      } catch (error) {
        if (error instanceof Error) {
          setToast({
            text: error.message,
            variant: 'error',
            icon: 'close-outline',
          });
        }
        return;
      }
    }

    const managedMembers: ManagedMemberInput[] = [];

    for (const member of values.members) {
      if (!member.id) {
        setToast({
          text: 'Could not prepare member profile',
          variant: 'error',
          icon: 'close-outline',
        });
        return;
      }

      const memberNameParts = member.title?.trim().split(/\s+/) ?? [];

      managedMembers.push({
        avatarId: newMemberAvatars[member.id] ?? null,
        color: member.color ?? null,
        firstName: memberNameParts[0] ?? '',
        id: member.id,
        lastName: memberNameParts.slice(1).join(' ') || null,
      });
    }
    const didInsertMembers = await state.insertMembers(
      managedMembers,
      memberType,
    );

    if (didInsertMembers) {
      let text = 'Added Organization Members';
      if (state.entity.kind === 'team') {
        text = `Added Team ${memberType === 'participant' ? 'Participants' : 'Non Participants'}`;
      }
      notify({
        text,
        icon: 'checkmark-outline',
      });
      dismissAll();
      return;
    }

    setToast({
      text: 'Something Went Wrong',
      variant: 'error',
      icon: 'close-outline',
    });
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title:
            state.status === 'ready'
              ? `Add People to ${state.entity.title}`
              : 'Add People',
        }}
      />
      <ContentBoundary
        data={state.status === 'ready' ? state : null}
        loading={state.status === 'loading'}
        error={state.status === 'error'}
        errorMessage="Could not load members."
        onRetry={
          state.status === 'error' || state.status === 'not-found'
            ? state.onRetry
            : undefined
        }
        empty={
          state.status === 'not-found' ? (
            <Placeholder
              icon="refresh-outline"
              text="Member List Not Found"
              subText="Tap to try again."
              onPress={state.onRetry}
            />
          ) : null
        }
      >
        {(state) => (
          <>
            <ModalKeyboardAwareScrollView>
              <MemberAddInvite
                entity={state.entity}
                initialMemberType={initialMemberType}
                setToast={setToast}
              />
              <MemberAddList>
                {state.entity.kind === 'team' ? (
                  <Box direction="vertical" space="sm" p="lg">
                    <MemberTypeSelector
                      testIDPrefix="TeamPersonAddManualMemberType"
                      value={memberType}
                      onValueChange={setMemberType}
                    />
                  </Box>
                ) : null}
              </MemberAddList>
            </ModalKeyboardAwareScrollView>
            <ModalKeyboardStickyView>
              <ButtonToast {...toast} />
              <FormSaveButton
                onPress={form.handleSubmit(onPressSave)}
                info={{ text: 'Save' }}
              />
            </ModalKeyboardStickyView>
          </>
        )}
      </ContentBoundary>
    </FormProvider>
  );
}
